/**
 * @module ui.product
 * @version 5.6.1
 * @description Product Card Web Component - Variant Selection & Add-to-Cart
 * 
 * @element liftr-product
 * 
 * @fires liftr:product:variant-selected - Variant changed
 * @fires liftr:product:added - Added to cart
 * @fires liftr:product:error - Error occurred
 * 
 * @attr {string} liftr-id - Product ID
 * @attr {string} liftr-state - State output (loading, unavailable, soldout)
 * 
 * Roles:
 * - product-option:              Option selector (select, radio group)
 * - product-price:               Current price display
 * - product-compare:             Compare-at price display
 * - product-discount:            Discount badge/percentage
 * - product-unit-price:          Unit price display (Grundpreis)
 * - product-quantity:            Quantity input
 * - product-add:                 Add to cart button (direct, no panel)
 * - product-add-text:            Button text element
 * - product-image:               Main product image
 * - product-sku:                 SKU display
 * - product-availability:        Availability text
 * - product-live:                Live region for screen reader announcements
 * 
 * Variant Panel Roles (Nested Pattern):
 * - product-variant-toggle:      Button that opens variant panel
 * - product-variant-panel:       liftr-panel element (calls Public API)
 * - product-variant-add:         Add to cart button inside panel
 * - product-variant-chip:        Variant chip buttons in panel
 * - product-variant-stock:       Stock indicator container in panel
 * - product-variant-image:       Variant image in panel
 * - product-variant-cta-price:   Price display in CTA button
 * - product-variant-selected:    Selected option label display
 * - product-stock-indicator:     Stock bar element (inside stock container)
 * - product-stock-label:          Stock text element (inside stock indicator)
 * - product-stock-refresh:       Refresh timestamp label (ticking relative time)
 * - product-stock-refresh-action: Manual refresh button (shown after threshold)
 * 
 * Context Bar Roles (inside variant panel, per option dimension):
 * - product-variant-unit-price:  Unit price text (e.g. "€99,50 / 100g")
 * - product-variant-savings:     Savings text + liftr-state (nudge|saved)
 * 
 * Data Attributes on Chips (Framework):
 * - liftr-field="variant"        → liftr-value = Variant ID
 * - liftr-field="option-{n}"     → liftr-value = Option value string
 * 
 * @changelog
 * v5.6.1 - FIX: Relative ES-Imports -> Bare Specifier (Import Map / Cache-Bust)
 *        - './api.shop.js' etc. umgingen die Import Map -> unversioniert, dauerhaft gecacht
 * v5.6.0 - NEW: Emits liftr:product:adding before cart.add()
 *        - Coordination signal for cart to defer reward notification flush
 *        - try/catch guarantees ADDED or ERROR always fires (no timeout needed)
 * v5.5.1 - FIX: Stock flicker during add-to-cart animation
 *        - #handleCartChanged() guards with #isAdding (no mid-animation stock update)
 *        - Success path: #syncPanelStock() before panel close (authoritative sync)
 *        - Error path: #syncPanelStock() after error animation (restore correct state)
 * v5.5.0 - FIX: Cart-aware availability + Shopify error passthrough
 *        - Added LISTEN: SHOP_CART_ADDED, SHOP_CART_UPDATED, SHOP_CART_CLEARED
 *        - Added #handleCartChanged() — re-syncs button + stock on cart events
 *        - Added #getCartQuantity(variantId) — sums all line items for variant
 *        - Added #getEffectiveInventory(variant) — stock minus cart quantity
 *        - #syncAddButton() uses effective inventory (button disables at 0)
 *        - #syncPanelStock() uses effective inventory (stock bar reflects cart)
 *        - #handleAddToCart() guard checks effective inventory before request
 *        - Error notification passes error.message as body (Shopify message visible)
 *        - Removed ALL i18n fallback strings (missing labels surface immediately)
 * v5.4.0 - NEW: Manual stock refresh action button
 *        - Added ROLES.STOCK_REFRESH_TOGGLE
 *        - Added REFRESH_ACTION_THRESHOLD (3 min), SECTION_STOCK constant
 *        - Added #productSlug (product URL handle for Section Rendering fetch)
 *        - Added #isRefreshing guard
 *        - Added #handleStockRefresh() — Section Rendering via product-stock-api
 *        - Added #updateStockIndicator() — stock state update helper for variant changes
 *        - Button shown after 3 min, hidden after refresh (DOM replaced)
 *        - Loading state on variant panel (skeleton), not button
 *        - Section Rendering replaces stock indicator DOM, timer re-inits
 *        - i18n: stock_refresh_action (via Liquid, not JS labels)
 *        - Requires: sections/product-stock-api.liquid
 * v5.3.0 - NEW: Stock refresh timer (relative timestamp on stock indicator)
 *        - Added ROLES.STOCK_REFRESH
 *        - Added #refreshTimestamp, #refreshInterval
 *        - Added #startRefreshTimer(), #updateRefreshLabel()
 *        - i18n: stock_refresh_now, stock_refresh_minutes (via product labels)
 *        - Section Render re-mount resets timer automatically ✓
 * v5.2.0 - NEW: Context Bar — dynamic unit price + savings on variant change
 *        - Added ROLES: VARIANT_UNIT_PRICE, VARIANT_SAVINGS
 *        - Added #syncContextBar() called from #syncPanelUI()
 *        - Added #allVariants cache from product data at mount
 *        - Added #contextBarCache for baseline + ordered values
 *        - Requires unit_price + unit_price_measurement in Product JSON
 * v5.1.2 - AUDIT COMPLIANCE - Quality Checks v4.8
 *        - Added LISTEN constant (1 external event → named constant)
 *        - Migrated standalone createState() → this.createState() (6 calls)
 *        - Removed createState from core.utilities.js import
 *        - Updated export to include LISTEN
 * v5.1.1 - FIX: Overlap success state with panel close animation
 *        - Success ✓ stays visible DURING panel exit (no double-disappear)
 *        - Panel exit duration read via getExitDuration() (fallback 150ms)
 *        - Clear success state AFTER panel is fully gone
 *        - Notification fires after panel close completes
 * v5.1.0 - REFACTOR: Use base class fx-loading helpers
 *        - awaitLoading(), awaitResult() from LiftrModule
 *        - STATES.ADDING → STATES.LOADING/SUCCESS/ERROR
 *        - Loading state on BUTTON via createState(), not host
 *        - Panel closes AFTER success animation completes
 *        - Notification fires AFTER panel close
 * v5.0.0 - AUDIT: Full v4.7 compliance
 *        - ROLES/STATES/EVENTS/SETTINGS/PROPERTIES constants
 *        - All string literals replaced with constants
 *        - Custom liftr-* attributes → liftr-field/liftr-value
 *        - Child state via createState() instead of custom helpers
 *        - is-selected CSS class → liftr-state token
 *        - querySelector → this.role() with scoped roles
 *        - VARIANT_SELECTED role prefix fixed
 *        - Removed unused liftr-product-index attribute
 *        - Removed STOCK_STATUS (merged into STATES)
 * v4.4.0 - FIX: Replace querySelector('.css-class') with liftr-role queries
 * v4.3.0 - FIX: Dynamic stock update in variant panel on chip click
 * v4.2.0 - NEW: Nested Panel Pattern for variant selection
 * v4.1.0 - Visual notifications via Notification API
 * v4.0.0 - Initial LIFTR v4.0 implementation
 * 
 * @example
 * <liftr-product liftr-id="{{ product.id }}">
 *   <!-- Direct ATC (single variant) -->
 *   <button liftr-role="product-add">Add</button>
 *   
 *   <!-- OR: Variant Panel (multi-variant) -->
 *   <button liftr-role="product-variant-toggle">Select</button>
 *   <liftr-panel liftr-role="product-variant-panel">
 *     <dialog>
 *       <button liftr-role="product-variant-chip"
 *               liftr-field="variant" liftr-value="12345678">50g</button>
 *       <button liftr-role="product-variant-add">
 *         <span>Add to Cart</span>
 *         <span liftr-role="product-variant-cta-price">€9,90</span>
 *       </button>
 *     </dialog>
 *   </liftr-panel>
 * </liftr-product>
 */

import { LiftrModule } from 'core.module';
import { ATTRIBUTES, FX, on, i18n, settings } from 'core.liftr';
import { ensureLive, updateImage } from 'core.utilities';
import { TYPES as NOTIFICATION_TYPES, DURATIONS } from 'api.notification';
import 'api.shop'; // Side-effect: registers window.liftrShop

/* ═══════════════════════════════════════════════════════════════════════════
 * MODULE CONSTANTS
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Role constants for DOM queries.
 * ALWAYS use ROLES.* in this.role() / this.roleAll().
 */
const ROLES = Object.freeze({
  // Price & Info
  OPTION:             'product-option',
  PRICE:              'product-price',
  COMPARE:            'product-compare',
  DISCOUNT:           'product-discount',
  UNIT_PRICE:         'product-unit-price',
  QUANTITY:           'product-quantity',
  IMAGE:              'product-image',
  SKU:                'product-sku',
  AVAILABILITY:       'product-availability',
  LIVE:               'product-live',

  // Direct Add (single variant)
  ADD:                'product-add',

  // Variant Panel (Nested Pattern)
  VARIANT_TOGGLE:     'product-variant-toggle',
  VARIANT_PANEL:      'product-variant-panel',
  VARIANT_CHIP:       'product-variant-chip',
  VARIANT_ADD:        'product-variant-add',
  VARIANT_STOCK:      'product-variant-stock',
  VARIANT_IMAGE:      'product-variant-image',
  VARIANT_CTA_PRICE:  'product-variant-cta-price',
  VARIANT_SELECTED:   'product-variant-selected',

  // Stock sub-elements (inside VARIANT_STOCK)
  STOCK_INDICATOR:      'product-stock-indicator',
  STOCK_LABEL:          'product-stock-label',
  STOCK_REFRESH:        'product-stock-refresh',
  STOCK_REFRESH_TOGGLE: 'product-stock-refresh-toggle',

  // Context Bar (unit price + savings per option dimension)
  VARIANT_UNIT_PRICE: 'product-variant-unit-price',
  VARIANT_SAVINGS:    'product-variant-savings'
});

/**
 * State tokens for liftr-state attribute.
 */
const STATES = Object.freeze({
  // Module-level (on host)
  UNAVAILABLE:    'unavailable',
  SOLDOUT:        'soldout',

  // fx-loading states (on button via createState)
  LOADING:        'loading',
  SUCCESS:        'success',
  ERROR:          'error',

  // Child-level
  SALE:           'sale',
  SELECTED:       'selected',

  // Stock status (exclusive, on stock indicator)
  STOCK_AVAILABLE:  'stock-available',
  STOCK_LOW:        'stock-low',
  STOCK_CRITICAL:   'stock-critical',
  STOCK_SOLDOUT:    'stock-soldout',
});

/**
 * Event actions for this.emit().
 */
const EVENTS = Object.freeze({
  VARIANT_SELECTED: 'variant-selected',
  ADDING:           'adding',
  ADDED:            'added',
  ERROR:            'error'
});

/**
 * External events this module listens to.
 * @type {Readonly<Object.<string, string>>}
 */
const LISTEN = Object.freeze({
  SHOP_VARIANT_CHANGED: 'liftr:shop:variant-changed',
  SHOP_CART_ADDED:      'liftr:shop:cart-added',
  SHOP_CART_UPDATED:    'liftr:shop:cart-updated',
  SHOP_CART_CLEARED:    'liftr:shop:cart-cleared',
});

/**
 * Module-specific settings (liftr-settings tokens).
 */
const SETTINGS = Object.freeze({
  // Reserved for future use
});

/**
 * CSS Custom Properties written by JavaScript.
 */
const PROPERTIES = Object.freeze({
  STOCK_SCALE: '--product-stock-scale'
});

/** Refresh timer interval in milliseconds (1 minute) */
const REFRESH_INTERVAL = 60000;

/** Minutes before showing manual refresh button */
const REFRESH_ACTION_THRESHOLD = 3;

/** Section ID for stock refresh via Section Rendering API */
const SECTION_STOCK = 'api-product-stock';

/* ═══════════════════════════════════════════════════════════════════════════
 * PRODUCT COMPONENT
 * ═══════════════════════════════════════════════════════════════════════════ */

class LiftrProduct extends LiftrModule {

  static NAME = 'product';

  static DEFAULTS = {};

  #productHandle = null;
  #productSlug = null;
  #isAdding = false;
  #isRefreshing = false;
  #labels = null;
  #liveRegion = null;

  /** @type {LiftrPanel|null} Nested variant panel */
  #variantPanel = null;

  /** @type {Object[]} All product variants (cached from product data) */
  #allVariants = [];

  /** @type {Map<string, { baseline: number, orderedValues: string[] }>} */
  #contextBarCache = new Map();

  /** @type {number} Timestamp of last stock data (mount or manual refresh) */
  #refreshTimestamp = 0;

  /** @type {number|null} Interval ID for refresh label */
  #refreshInterval = null;

  /** @type {Function|null} Cleanup for current action button click handler */
  #refreshActionCleanup = null;

  /* ─────────────────────────────────────────────────────────────────────────
   * LIFECYCLE
   * ───────────────────────────────────────────────────────────────────────── */

  mount() {
    // Get i18n labels
    this.#labels = i18n.get('product') || {};

    // Setup live region
    this.#setupLiveRegion();

    const productId = this.getAttribute(ATTRIBUTES.ID);
    if (!productId) {
      throw new Error('liftr-product requires liftr-id attribute');
    }

    // Get product data from script tag or window
    const productData = this.#getProductData(productId);
    if (!productData) {
      throw new Error(`Product data not found for ID: ${productId}`);
    }

    // Cache all variants for context bar calculations
    this.#allVariants = productData.variants || [];

    // Cache product URL handle for manual refresh fetch
    this.#productSlug = productData.handle || null;

    // Register with shop API
    this.#productHandle = window.liftrShop.products.register(productId, productData);

    // Bind option selectors (select/radio - legacy support)
    this.#bindOptionSelectors();

    // Bind direct add button (single variant products)
    this.#bindAddButton();

    // Bind quantity input
    this.#bindQuantityInput();

    // ═══════════════════════════════════════════════════════════════════════
    // NESTED PANEL PATTERN
    // ═══════════════════════════════════════════════════════════════════════

    // Query nested panel via OWN role
    this.#variantPanel = this.role(ROLES.VARIANT_PANEL);

    // Bind variant toggle button → opens panel
    this.#bindVariantToggle();

    // Bind variant chips inside panel
    this.#bindVariantChips();

    // Bind variant add button inside panel
    this.#bindVariantAdd();

    // Listen for variant changes from API
    this.addDisposable(
      on(LISTEN.SHOP_VARIANT_CHANGED, this.#handleVariantChanged.bind(this))
    );

    // Listen for cart changes → re-sync effective availability
    const cartHandler = this.#handleCartChanged.bind(this);
    this.addDisposable(on(LISTEN.SHOP_CART_ADDED, cartHandler));
    this.addDisposable(on(LISTEN.SHOP_CART_UPDATED, cartHandler));
    this.addDisposable(on(LISTEN.SHOP_CART_CLEARED, cartHandler));

    // Initial UI state
    this.#syncVariantUI(this.#productHandle.getVariant());

    // Start stock refresh timer + bind action button (if elements exist)
    this.#startRefreshTimer();
    this.addDisposable(() => this.#teardownRefreshTimer());
  }

  unmount() {
    if (this.#productHandle) {
      this.#productHandle.unregister();
      this.#productHandle = null;
    }
    this.#productSlug = null;
    this.#variantPanel = null;
    this.#allVariants = [];
    this.#contextBarCache.clear();
    this.state.clear();
    super.unmount();
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * ARIA / LIVE REGION
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Setup live region for screen reader announcements
   */
  #setupLiveRegion() {
    this.#liveRegion = this.role(ROLES.LIVE);

    if (!this.#liveRegion) {
      this.#liveRegion = document.createElement('div');
      this.#liveRegion.setAttribute(ATTRIBUTES.ROLE, ROLES.LIVE);
      this.#liveRegion.className = 'sr-only';
      ensureLive(this.#liveRegion);
      this.prepend(this.#liveRegion);
    } else {
      ensureLive(this.#liveRegion);
    }
  }

  /**
   * Announce message to screen readers
   * @param {string} message
   */
  #announce(message) {
    if (!this.#liveRegion) return;
    this.#liveRegion.textContent = message;
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * NOTIFICATIONS
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Show visual notification via Notification API.
   * @param {string} type - Notification type (success, error, info)
   * @param {string} title - Notification title
   * @param {string|null} [message] - Optional message body
   * @param {Object} [options] - Additional options (duration)
   */
  #notify(type, title, options = {}) {
    if (!window.liftrNotification) return;

    const method = window.liftrNotification[type];
    if (typeof method !== 'function') return;

    method.call(window.liftrNotification, title, {
      duration: options.duration ?? DURATIONS.NORMAL,
      ...options
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * DATA RETRIEVAL
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Get product data from embedded JSON or global
   * @param {string} productId
   * @returns {Object|null}
   */
  #getProductData(productId) {
    // Try embedded script first
    const script = this.querySelector('script[type="application/json"]');
    if (script) {
      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        // Fall through to other methods
      }
    }

    // Try global product data
    if (window.productData?.[productId]) {
      return window.productData[productId];
    }

    // Try Shopify global (PDP)
    if (window.ShopifyProduct) {
      return window.ShopifyProduct;
    }

    return null;
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * EVENT BINDING - Legacy (Select/Radio)
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Bind option selectors (select, radio) - for backwards compatibility
   */
  #bindOptionSelectors() {
    const optionEls = this.roleAll(ROLES.OPTION);

    optionEls.forEach((el, index) => {
      if (el.tagName === 'SELECT') {
        const handler = (e) => this.#handleOptionChange(index, e.target.value);
        el.addEventListener('change', handler);
        this.addDisposable(() => el.removeEventListener('change', handler));
      } else {
        const radios = el.querySelectorAll('input[type="radio"]');
        radios.forEach(radio => {
          const handler = (e) => {
            if (e.target.checked) {
              this.#handleOptionChange(index, e.target.value);
            }
          };
          radio.addEventListener('change', handler);
          this.addDisposable(() => radio.removeEventListener('change', handler));
        });
      }
    });
  }

  /**
   * Bind direct add to cart button (single variant products)
   */
  #bindAddButton() {
    const addBtn = this.role(ROLES.ADD);
    if (!addBtn) return;

    const handler = (e) => {
      e.preventDefault();
      this.#handleAddToCart();
    };

    addBtn.addEventListener('click', handler);
    this.addDisposable(() => addBtn.removeEventListener('click', handler));
  }

  /**
   * Bind quantity input
   */
  #bindQuantityInput() {
    const qtyInput = this.role(ROLES.QUANTITY);
    if (!qtyInput) return;

    const handler = () => {
      const min = parseInt(qtyInput.min, 10) || 1;
      const max = parseInt(qtyInput.max, 10) || 9999;
      let value = parseInt(qtyInput.value, 10) || 1;

      value = Math.max(min, Math.min(max, value));
      qtyInput.value = value;
    };

    qtyInput.addEventListener('change', handler);
    qtyInput.addEventListener('blur', handler);
    this.addDisposable(() => {
      qtyInput.removeEventListener('change', handler);
      qtyInput.removeEventListener('blur', handler);
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * EVENT BINDING - Nested Panel Pattern
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Bind variant toggle button → opens panel via Public API
   */
  #bindVariantToggle() {
    const toggle = this.role(ROLES.VARIANT_TOGGLE);
    if (!toggle || !this.#variantPanel) return;

    const handler = (e) => {
      e.preventDefault();
      this.#variantPanel.open();
    };

    toggle.addEventListener('click', handler);
    this.addDisposable(() => toggle.removeEventListener('click', handler));
  }

  /**
   * Bind variant chip buttons inside panel.
   * Chips use liftr-field/liftr-value:
   *   liftr-field="variant"   → liftr-value = Variant ID
   *   liftr-field="option-0"  → liftr-value = Option value
   */
  #bindVariantChips() {
    const chips = this.roleAll(ROLES.VARIANT_CHIP);
    if (!chips.length) return;

    chips.forEach(chip => {
      const handler = (e) => {
        e.preventDefault();
        this.#handleChipClick(chip);
      };

      chip.addEventListener('click', handler);
      this.addDisposable(() => chip.removeEventListener('click', handler));
    });
  }

  /**
   * Bind variant add button inside panel
   */
  #bindVariantAdd() {
    const addBtn = this.role(ROLES.VARIANT_ADD);
    if (!addBtn) return;

    const handler = (e) => {
      e.preventDefault();
      this.#handleAddToCart(true); // closePanel = true
    };

    addBtn.addEventListener('click', handler);
    this.addDisposable(() => addBtn.removeEventListener('click', handler));
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * USER INTERACTIONS
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Handle option selector change (select/radio)
   * @param {number} optionIndex
   * @param {string} value
   */
  #handleOptionChange(optionIndex, value) {
    const result = this.#productHandle.selectOption(optionIndex, value);

    if (result.success) {
      this.emit(EVENTS.VARIANT_SELECTED, {
        variant: result.variant,
        optionIndex,
        value
      });
    }
  }

  /**
   * Handle variant chip click.
   * Reads liftr-field / liftr-value to determine selection mode.
   * @param {HTMLElement} chip
   */
  #handleChipClick(chip) {
    if (chip.disabled || chip.getAttribute('aria-disabled') === 'true') {
      return;
    }

    const field = chip.getAttribute(ATTRIBUTES.FIELD);
    const value = chip.getAttribute(ATTRIBUTES.VALUE);

    if (!field || !value) return;

    let result;
    let optionIndex;

    if (field === 'variant') {
      // Direct variant selection: liftr-value = Variant ID
      result = this.#productHandle.selectVariant(parseInt(value, 10));
      optionIndex = null;
    } else if (field.startsWith('option-')) {
      // Option-based selection: liftr-field="option-{n}", liftr-value = option string
      optionIndex = parseInt(field.split('-')[1], 10);
      if (isNaN(optionIndex)) return;
      result = this.#productHandle.selectOption(optionIndex, value);
    } else {
      return;
    }

    if (result.success) {
      // Update chip states
      this.#syncChipStates(result.variant);

      // Update panel UI (price, stock, context bar)
      this.#syncPanelUI(result.variant);

      this.emit(EVENTS.VARIANT_SELECTED, {
        variant: result.variant,
        optionIndex,
        value
      });
    }
  }

  /**
   * Handle add to cart with fx-loading state flow.
   * Uses base class helpers: awaitLoading(), awaitResult().
   *
   * Cart events (SHOP_CART_ADDED etc.) are suppressed via #isAdding guard
   * in #handleCartChanged() to prevent mid-animation stock flicker.
   * Authoritative sync happens here AFTER animation completes.
   * 
   * @param {boolean} [closePanel=false] - Close panel after success
   */
  async #handleAddToCart(closePanel = false) {
    if (this.#isAdding) return;

    const variant = this.#productHandle.getVariant();
    if (!variant?.available || this.#getEffectiveInventory(variant) <= 0) return;

    const btn = closePanel
      ? this.role(ROLES.VARIANT_ADD)
      : this.role(ROLES.ADD);

    const qtyInput = this.role(ROLES.QUANTITY);
    const quantity = parseInt(qtyInput?.value, 10) || 1;

    this.#isAdding = true;

    const btnState = btn ? this.createState(btn) : null;
    btnState?.add(STATES.LOADING);

    const startTime = performance.now();

    try {
      
      this.emit(EVENTS.ADDING, { variant, quantity }); 
      
      await window.liftrShop.cart.add({
        id: variant.id,
        quantity
      });

      // ① Enforce MIN_LOADING
      await this.awaitLoading(startTime);

      // ② LOADING → SUCCESS crossfade
      btnState?.remove(STATES.LOADING);
      btnState?.add(STATES.SUCCESS);

      // ③ Wait for result display (entry + result-duration)
      await this.awaitResult(btn);

      // ④ Wait for SLIDE exit animation (label slides back)
      await this.awaitResultExit(btn, btnState);

      // ⑤ Button is fully idle → safe to close panel
      if (closePanel && this.#variantPanel) {
        this.#variantPanel.close();
      }

      // ⑥ Reset + side effects AFTER button idle
      this.#syncAddButton(variant);
      this.#syncPanelStock(variant);
      if (qtyInput) qtyInput.value = 1;

      this.emit(EVENTS.ADDED, { variant, quantity });
      this.#announce(this.#labels.item_added);
      this.#notify(NOTIFICATION_TYPES.SUCCESS, this.#labels.item_added, { duration: DURATIONS.SHORT });

    } catch (error) {
      // Error: Always Full Cycle
      await this.awaitLoading(startTime);

      btnState?.remove(STATES.LOADING);
      btnState?.add(STATES.ERROR);

      await this.awaitResult(btn, true);
      await this.awaitResultExit(btn, btnState, STATES.ERROR);

      this.#syncAddButton(variant);
      this.#syncPanelStock(variant);

      this.emit(EVENTS.ERROR, { action: 'add', error });
      this.#notify(NOTIFICATION_TYPES.ERROR, error.message || this.#labels.add_error)

    } finally {
      this.#isAdding = false;
    }
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * SHOP EVENT HANDLERS
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Handle variant changed event from API
   * @param {CustomEvent} e
   */
  #handleVariantChanged(e) {
    const { productId, variant } = e.detail;

    if (productId !== this.getAttribute(ATTRIBUTES.ID)) return;

    this.#syncVariantUI(variant);
    this.#syncPanelUI(variant);
  }

  /**
   * Handle cart change — re-sync button + stock with effective inventory.
   * Listens to Data-layer events (cart-added, cart-updated, cart-cleared).
   *
   * Guarded by #isAdding: during add-to-cart animation, cart events are
   * suppressed to prevent stock flicker. The authoritative sync happens
   * in #handleAddToCart() after the animation completes.
   */
  #handleCartChanged() {
    if (this.#isAdding) return;
    const variant = this.#productHandle?.getVariant();
    if (!variant) return;
    this.#syncAddButton(variant);
    this.#syncPanelStock(variant);
  }

  /**
   * Get total quantity of a variant currently in cart.
   * Sums across all line items (subscription + one-time are separate lines).
   * @param {number} variantId
   * @returns {number}
   */
  #getCartQuantity(variantId) {
    const cart = window.liftrShop?.cart?.get?.();
    if (!cart?.items) return 0;
    return cart.items
      .filter(item => item.variant_id === variantId)
      .reduce((sum, item) => sum + item.quantity, 0);
  }

  /**
   * Get effective inventory: physical stock minus cart quantity.
   * Returns Infinity for untracked inventory (no limit).
   * @param {Object} variant
   * @returns {number}
   */
  #getEffectiveInventory(variant) {
    if (!variant) return 0;
    if (variant.inventory_management !== 'shopify') return Infinity;
    const stock = variant.inventory_quantity ?? 0;
    const inCart = this.#getCartQuantity(variant.id);
    return Math.max(0, stock - inCart);
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * UI SYNC - Main Card
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Sync all UI elements with variant
   * @param {Object} variant
   */
  #syncVariantUI(variant) {
    this.#syncPrice(variant);
    this.#syncAddButton(variant);
    this.#syncImage(variant);
    this.#syncMeta(variant);
    this.#syncOptionAvailability();
    this.#updateURL(variant);

    if (variant) {
      const price = window.liftrShop.pricing.format(variant.price);
      const msg = (this.#labels.price_changed).replace('{{ price }}', price);
      this.#announce(msg);
    }
  }

  /**
   * Sync price display
   * @param {Object} variant
   */
  #syncPrice(variant) {
    const priceEl = this.role(ROLES.PRICE);
    const compareEl = this.role(ROLES.COMPARE);
    const discountEl = this.role(ROLES.DISCOUNT);
    const unitPriceEl = this.role(ROLES.UNIT_PRICE);

    if (!variant) return;

    const price = window.liftrShop.pricing.format(variant.price);
    const isOnSale = window.liftrShop.pricing.isOnSale(variant);

    if (priceEl) {
      priceEl.textContent = price;
      this.createState(priceEl).toggle(STATES.SALE, isOnSale);
    }

    if (compareEl) {
      if (isOnSale) {
        compareEl.textContent = window.liftrShop.pricing.format(variant.compare_at_price);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }

    if (discountEl) {
      if (isOnSale) {
        const discount = window.liftrShop.pricing.calculateDiscount(
          variant.compare_at_price,
          variant.price
        );
        discountEl.textContent = `-${discount}%`;
        discountEl.hidden = false;
      } else {
        discountEl.hidden = true;
      }
    }

    if (unitPriceEl) {
      if (window.liftrShop.pricing.hasUnitPrice(variant)) {
        unitPriceEl.textContent = window.liftrShop.pricing.formatUnitPrice(variant);
        unitPriceEl.hidden = false;
      } else {
        unitPriceEl.hidden = true;
      }
    }
  }

  /**
   * Sync add button state.
   * Uses effective inventory (stock minus cart) to determine availability.
   * @param {Object} variant
   */
  #syncAddButton(variant) {
    const addBtn = this.role(ROLES.ADD);
    const variantAddBtn = this.role(ROLES.VARIANT_ADD);
    const toggleBtn = this.role(ROLES.VARIANT_TOGGLE);

    const effectivelyAvailable = variant?.available && this.#getEffectiveInventory(variant) > 0;

    // Direct add button
    if (addBtn) {
      if (!variant) {
        addBtn.disabled = true;
        this.state.add(STATES.UNAVAILABLE);
        this.state.remove(STATES.SOLDOUT);
      } else if (!effectivelyAvailable) {
        addBtn.disabled = true;
        this.state.add(STATES.SOLDOUT);
        this.state.remove(STATES.UNAVAILABLE);
      } else {
        addBtn.disabled = false;
        this.state.remove(STATES.UNAVAILABLE);
        this.state.remove(STATES.SOLDOUT);
      }
    }

    // Variant toggle button (disable if all sold out)
    if (toggleBtn) {
      toggleBtn.disabled = !effectivelyAvailable;
    }

    // Variant add button inside panel
    if (variantAddBtn) {
      variantAddBtn.disabled = !effectivelyAvailable;
    }
  }

  /**
   * Sync main product image
   * @param {Object} variant
   */
  #syncImage(variant) {
    if (!variant) return;
    updateImage(this.role(ROLES.IMAGE), variant.featured_image, [150, 300, 450, 600]);
  }

  /**
   * Sync meta information (SKU, availability)
   * @param {Object} variant
   */
  #syncMeta(variant) {
    const skuEl = this.role(ROLES.SKU);
    if (skuEl && variant?.sku) {
      skuEl.textContent = variant.sku;
    }

    const availabilityEl = this.role(ROLES.AVAILABILITY);
    if (availabilityEl && variant) {
      const elState = this.createState(availabilityEl);
      if (variant.available) {
        availabilityEl.textContent = this.#labels.in_stock;
        elState.remove(STATES.UNAVAILABLE);
      } else {
        availabilityEl.textContent = this.#labels.out_of_stock;
        elState.add(STATES.UNAVAILABLE);
      }
    }
  }

  /**
   * Update option selector availability indicators (select/radio)
   */
  #syncOptionAvailability() {
    const optionEls = this.roleAll(ROLES.OPTION);

    optionEls.forEach((el, index) => {
      if (el.tagName === 'SELECT') {
        Array.from(el.options).forEach(option => {
          const available = this.#productHandle.isOptionAvailable(index, option.value);
          option.disabled = !available;
          this.createState(option).toggle(STATES.UNAVAILABLE, !available);
        });
      } else {
        const radios = el.querySelectorAll('input[type="radio"]');
        radios.forEach(radio => {
          const available = this.#productHandle.isOptionAvailable(index, radio.value);
          radio.disabled = !available;
          const label = radio.closest('label');
          if (label) {
            this.createState(label).toggle(STATES.UNAVAILABLE, !available);
          }
        });
      }
    });
  }

  /**
   * Update URL with variant parameter (PDP only)
   * @param {Object} variant
   */
  #updateURL(variant) {
    if (!variant?.id) return;

    // Only update URL on product detail pages
    if (!window.location.pathname.includes('/products/')) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);

    window.history.replaceState({}, '', url.toString());
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * UI SYNC - Variant Panel
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Sync chip selected states.
   * Reads liftr-field / liftr-value to determine match.
   * @param {Object} variant
   */
  #syncChipStates(variant) {
    if (!variant) return;

    const chips = this.roleAll(ROLES.VARIANT_CHIP);

    chips.forEach(chip => {
      const field = chip.getAttribute(ATTRIBUTES.FIELD);
      const value = chip.getAttribute(ATTRIBUTES.VALUE);

      let isSelected = false;

      if (field === 'variant') {
        // Direct variant chip
        isSelected = parseInt(value, 10) === variant.id;
      } else if (field?.startsWith('option-')) {
        // Option value chip - check if variant has this option
        const optionIndex = parseInt(field.split('-')[1], 10);
        const variantOptions = variant.options || [];
        isSelected = variantOptions[optionIndex] === value;
      }

      // Update ARIA and state
      chip.setAttribute('aria-checked', isSelected);
      this.createState(chip).toggle(STATES.SELECTED, isSelected);
    });

    // Update "selected" label displays
    const selectedLabels = this.roleAll(ROLES.VARIANT_SELECTED);
    selectedLabels.forEach(label => {
      const field = label.closest(`[${ATTRIBUTES.FIELD}]`)?.getAttribute(ATTRIBUTES.FIELD);
      if (field?.startsWith('option-')) {
        const optionIndex = parseInt(field.split('-')[1], 10);
        if (!isNaN(optionIndex) && variant.options?.[optionIndex]) {
          label.textContent = `· ${variant.options[optionIndex]}`;
        }
      }
    });
  }

  /**
   * Sync panel UI elements (price in CTA, stock indicator, context bar)
   * @param {Object} variant
   */
  #syncPanelUI(variant) {
    if (!variant || !this.#variantPanel) return;

    const panelImg = this.role(ROLES.VARIANT_IMAGE);
    if (panelImg) {
      const imgSrc = typeof variant.featured_image === 'string'
        ? variant.featured_image
        : variant.featured_image?.src;

      if (imgSrc && panelImg.src !== imgSrc) {
        panelImg.src = imgSrc;
        panelImg.removeAttribute('srcset');
      }
    }

    // Update CTA price via role
    const priceEl = this.role(ROLES.VARIANT_CTA_PRICE);
    if (priceEl) {
      priceEl.textContent = window.liftrShop.pricing.format(variant.price);
    }

    // Update variant ID + disabled on add button
    const addBtn = this.role(ROLES.VARIANT_ADD);
    if (addBtn) {
      addBtn.setAttribute(ATTRIBUTES.VALUE, variant.id);
      addBtn.disabled = !variant.available || this.#getEffectiveInventory(variant) <= 0;
    }

    // Dynamic stock update
    this.#syncPanelStock(variant);

    // Context bar: unit price + savings
    this.#syncContextBar(variant);
  }

  /**
   * Sync stock indicator in variant panel.
   * Uses effective inventory (stock minus cart) for display.
   * @param {Object} variant
   */
  #syncPanelStock(variant) {
    const stockContainer = this.role(ROLES.VARIANT_STOCK);
    if (!stockContainer) return;

    const inventoryManagement = variant.inventory_management;

    // No inventory tracking → hide
    if (!inventoryManagement || inventoryManagement !== 'shopify') {
      stockContainer.hidden = true;
      return;
    }

    stockContainer.hidden = false;

    // Query stock indicator via role (scoped to stock container)
    const stockEl = this.role(ROLES.STOCK_INDICATOR, stockContainer);
    if (!stockEl) return;

    const effectiveInventory = this.#getEffectiveInventory(variant);
    this.#updateStockIndicator(stockEl, effectiveInventory);
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * UI SYNC - Stock Indicator (Shared Helper)
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Update a stock indicator element with inventory data.
   * Sets state tokens, proportional scale, and text label.
   *
   * Used by #syncPanelStock() on variant change in panel.
   * Manual refresh uses Section Rendering (DOM replacement) instead.
   *
   * @param {HTMLElement} indicator - Stock indicator element (liftr-role="product-stock-indicator")
   * @param {number} inventory - Current inventory count
   */
  #updateStockIndicator(indicator, inventory) {
    const { threshold_warning, threshold_critical } = settings.market.stock;

    // Status + label
    let status = STATES.STOCK_AVAILABLE;
    let text = this.#labels.in_stock;

    if (inventory <= 0) {
      status = STATES.STOCK_SOLDOUT;
      text = this.#labels.out_of_stock;
    } else if (inventory <= threshold_critical) {
      status = STATES.STOCK_CRITICAL;
      text = (this.#labels.stock_critical).replace('{{ count }}', inventory);
    } else if (inventory <= threshold_warning) {
      status = STATES.STOCK_LOW;
      text = (this.#labels.stock_low).replace('{{ count }}', inventory);
    }

    // Proportional scale: min(inventory / threshold_warning, 1.0) * 0.8
    let scale = 0;
    if (inventory > 0) {
      scale = Math.min(inventory / threshold_warning, 1) * 0.8;
      scale = Math.max(scale, 0.03);
    }

    // Set exclusive stock state (replace all)
    this.createState(indicator).set([status]);

    // Set proportional scale
    indicator.style.setProperty(PROPERTIES.STOCK_SCALE, scale.toFixed(2));

    // Update text
    const textEl = this.role(ROLES.STOCK_LABEL, indicator);
    if (textEl) {
      textEl.textContent = text;
    }
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * UI SYNC - Context Bar (Unit Price + Savings)
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Sync context bar with unit price and savings info.
   *
   * Queries VARIANT_UNIT_PRICE and VARIANT_SAVINGS directly (no container role).
   * Derives the option dimension from the nearest parent with liftr-field="option-{n}".
   *
   * States on savings element (exclusive):
   *   "saved" — current variant saves vs baseline (green)
   *   "nudge" — suggests next larger option (muted)
   *
   * @param {Object} variant - Currently selected variant
   */
  #syncContextBar(variant) {
    const unitPriceEls = this.roleAll(ROLES.VARIANT_UNIT_PRICE);
    if (!unitPriceEls.length) return;
    if (!variant?.unit_price || !variant.unit_price_measurement) return;

    unitPriceEls.forEach(unitPriceEl => {
      // Derive option dimension from nearest parent with liftr-field
      const fieldParent = unitPriceEl.closest(`[${ATTRIBUTES.FIELD}]`);
      const field = fieldParent?.getAttribute(ATTRIBUTES.FIELD);
      if (!field?.startsWith('option-')) return;

      const optionIndex = parseInt(field.split('-')[1], 10);
      if (isNaN(optionIndex)) return;

      // ── Compute baseline + ordered values (cached) ──
      const cacheKey = this.#buildContextCacheKey(variant, optionIndex);
      let cached = this.#contextBarCache.get(cacheKey);

      if (!cached) {
        cached = this.#computeContextData(variant, optionIndex);
        this.#contextBarCache.set(cacheKey, cached);
      }

      const { baseline, orderedValues } = cached;
      if (!baseline || baseline <= 0) return;

      // ── Update unit price text ──
      const formatted = window.liftrShop.pricing.format(variant.unit_price);
      const ref = variant.unit_price_measurement;
      const refText = ref.reference_value === 1
        ? ref.reference_unit
        : `${ref.reference_value}${ref.reference_unit}`;
      unitPriceEl.textContent = `${formatted} / ${refText}`;

      // ── Update savings (sibling within same parent) ──
      const savingsEl = fieldParent
        ? this.role(ROLES.VARIANT_SAVINGS, fieldParent)
        : null;
      if (!savingsEl) return;

      const currentUnitPrice = variant.unit_price;
      const savingsPct = currentUnitPrice < baseline
        ? Math.round((baseline - currentUnitPrice) / baseline * 100)
        : 0;

      const savingsState = this.createState(savingsEl);

      if (savingsPct > 0) {
        // State: saved — current variant has savings vs baseline
        const text = (this.#labels.savings_saved)
          .replace('{{ savings }}', savingsPct);

        savingsEl.textContent = text;
        savingsEl.hidden = false;
      } else {
        // State: nudge — suggest next larger option
        this.#renderNudge(variant, optionIndex, baseline, orderedValues, savingsEl, savingsState);
      }
    });
  }

  /**
   * Render nudge state on savings element.
   * Shows savings percentage of the next larger option value.
   * Hides element if no next value exists or has no savings.
   *
   * @param {Object} variant - Current variant
   * @param {number} optionIndex - Option dimension
   * @param {number} baseline - Highest unit price in dimension
   * @param {string[]} orderedValues - Option values in DOM order
   * @param {HTMLElement} savingsEl - Savings text element
   * @param {Object} savingsState - StateManager for savingsEl
   */
  #renderNudge(variant, optionIndex, baseline, orderedValues, savingsEl, savingsState) {
    const currentValue = variant.options?.[optionIndex];
    const currentIdx = orderedValues.indexOf(currentValue);
    const nextValue = currentIdx >= 0 && currentIdx < orderedValues.length - 1
      ? orderedValues[currentIdx + 1]
      : null;

    if (!nextValue) {
      savingsEl.hidden = true;
      savingsState.clear();
      return;
    }

    const nextVariant = this.#findVariantByOption(variant, optionIndex, nextValue);
    const nextUnitPrice = nextVariant?.unit_price ?? 0;

    if (nextUnitPrice > 0 && nextUnitPrice < baseline) {
      const nextSavingsPct = Math.round((baseline - nextUnitPrice) / baseline * 100);
      const unit = variant.unit_price_measurement.reference_unit;
      const text = (this.#labels.savings_nudge)
        .replace('{{ next }}', nextValue)
        .replace('{{ savings }}', nextSavingsPct)
        .replace('{{ unit }}', unit);

      savingsEl.textContent = text;
      savingsEl.hidden = false;
    } else {
      savingsEl.hidden = true;
      savingsState.clear();
    }
  }

  /**
   * Build cache key for context bar data.
   * Baseline depends on other option dimensions, not the target one.
   * @param {Object} variant
   * @param {number} optionIndex
   * @returns {string}
   */
  #buildContextCacheKey(variant, optionIndex) {
    const others = (variant.options || [])
      .map((v, i) => i === optionIndex ? '*' : v)
      .join('|');
    return `${optionIndex}:${others}`;
  }

  /**
   * Compute baseline unit price and ordered option values for a dimension.
   * Baseline = highest unit_price among variants matching OTHER option dimensions.
   * Ordered values = option values in chip DOM order (natural product ordering).
   *
   * @param {Object} variant - Current variant (for other-option matching)
   * @param {number} optionIndex - Target option dimension
   * @returns {{ baseline: number, orderedValues: string[] }}
   */
  #computeContextData(variant, optionIndex) {
    let baseline = 0;

    // Find highest unit_price among matching variants
    this.#allVariants.forEach(v => {
      if (!v.unit_price || v.unit_price <= 0) return;

      const matchesOtherOptions = (v.options || []).every((opt, i) =>
        i === optionIndex || opt === variant.options?.[i]
      );

      if (matchesOtherOptions && v.unit_price > baseline) {
        baseline = v.unit_price;
      }
    });

    // Get ordered values from chip DOM order
    const orderedValues = [];
    const chips = this.roleAll(ROLES.VARIANT_CHIP);
    chips.forEach(chip => {
      const field = chip.getAttribute(ATTRIBUTES.FIELD);
      const value = chip.getAttribute(ATTRIBUTES.VALUE);
      if (field === `option-${optionIndex}` && value && !orderedValues.includes(value)) {
        orderedValues.push(value);
      }
    });

    return { baseline, orderedValues };
  }

  /**
   * Find variant matching current variant's other options with a different
   * value for the target dimension.
   *
   * @param {Object} currentVariant - Reference for other options
   * @param {number} optionIndex - Dimension to swap
   * @param {string} targetValue - Desired value for that dimension
   * @returns {Object|null}
   */
  #findVariantByOption(currentVariant, optionIndex, targetValue) {
    return this.#allVariants.find(v =>
      v.options?.[optionIndex] === targetValue &&
      (v.options || []).every((opt, i) =>
        i === optionIndex || opt === currentVariant.options?.[i]
      )
    ) ?? null;
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * STOCK REFRESH TIMER + MANUAL REFRESH
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Tear down refresh timer and click handler.
   * Safe to call multiple times — idempotent.
   */
  #teardownRefreshTimer() {
    if (this.#refreshInterval) {
      clearInterval(this.#refreshInterval);
      this.#refreshInterval = null;
    }

    if (this.#refreshActionCleanup) {
      this.#refreshActionCleanup();
      this.#refreshActionCleanup = null;
    }
  }

  /**
   * Start refresh timer for stock indicator.
   * Sets initial label, starts 60s interval, and binds manual refresh button.
   *
   * After REFRESH_ACTION_THRESHOLD minutes, the action button is shown.
   * On manual refresh or Section Render re-mount, timer resets to 0.
   */
  #startRefreshTimer() {
    const refreshEl = this.role(ROLES.STOCK_REFRESH);
    if (!refreshEl) return;

    const actionBtn = this.role(ROLES.STOCK_REFRESH_TOGGLE);

    this.#refreshTimestamp = Date.now();
    this.#updateRefreshLabel(refreshEl, 0);

    // Ensure action button starts hidden
    if (actionBtn) {
      actionBtn.hidden = true;
    }

    this.#refreshInterval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - this.#refreshTimestamp) / REFRESH_INTERVAL);
      this.#updateRefreshLabel(refreshEl, elapsed);

      // Show action button after threshold
      if (actionBtn && elapsed >= REFRESH_ACTION_THRESHOLD) {
        actionBtn.hidden = false;
      }
    }, REFRESH_INTERVAL);

    // Bind manual refresh action
    if (actionBtn) {
      const handler = (e) => {
        e.preventDefault();
        this.#handleStockRefresh();
      };

      actionBtn.addEventListener('click', handler);
      this.#refreshActionCleanup = () => actionBtn.removeEventListener('click', handler);
    }
  }

  /**
   * Update refresh label text with elapsed minutes.
   * Uses this.#labels (= i18n.get('product')) for translations.
   *
   * @param {HTMLElement} el - Refresh label element
   * @param {number} minutes - Minutes since last refresh
   */
  #updateRefreshLabel(el, minutes) {
    if (minutes === 0) {
      el.textContent = this.#labels.stock_refresh_now;
    } else if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const template = hours === 1
        ? this.#labels.stock_refresh_hour
        : this.#labels.stock_refresh_hours;
      el.textContent = template.replace('{{ hours }}', hours);
    } else {
      const template = this.#labels.stock_refresh_minutes;
      el.textContent = template.replace('{{ minutes }}', minutes);
    }
  }

  /**
   * Handle manual stock refresh via Section Rendering API.
   *
   * Flow:
   *   1. Set loading state on variant panel (skeleton overlay)
   *   2. Fetch section HTML: /products/{slug}?variant={id}&section_id=product-stock-api
   *   3. Parse response, extract fresh stock indicator element
   *   4. Replace existing stock indicator in DOM
   *   5. Enforce MIN_LOADING, remove skeleton
   *   6. Clear old refresh interval, re-init timer on fresh DOM
   *
   * Why Section Rendering over Product JSON?
   *   /products/{handle}.js does NOT expose inventory_quantity (Admin-only).
   *   Section Rendering runs Liquid server-side with full variant access.
   *
   * Guards:
   *   - #isRefreshing prevents double-clicks
   *   - Missing #productSlug silently aborts (no fetch possible)
   *   - Missing #variantPanel silently aborts (no skeleton target)
   *   - Missing stock indicator in response throws (caught, skeleton removed)
   *   - Network errors caught, skeleton removed, button stays for retry
   */
  async #handleStockRefresh() {
    if (this.#isRefreshing || !this.#productSlug) return;

    const panel = this.#variantPanel;
    if (!panel) return;

    const currentVariant = this.#productHandle?.getVariant();
    if (!currentVariant?.id) return;

    this.#isRefreshing = true;

    const panelState = this.createState(panel);
    panelState.add(STATES.LOADING);

    const startTime = performance.now();

    try {
      const url = `/products/${this.#productSlug}?variant=${currentVariant.id}&section_id=${SECTION_STOCK}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const html = await response.text();

      // Parse response — Section Rendering wraps in <div id="shopify-section-...">
      const template = document.createElement('template');
      template.innerHTML = html;
      const freshIndicator = template.content.querySelector(
        `[${ATTRIBUTES.ROLE}~="${ROLES.STOCK_INDICATOR}"]`
      );

      if (!freshIndicator) {
        throw new Error('Stock indicator not found in section response');
      }

      // Replace existing indicator in DOM
      const stockContainer = this.role(ROLES.VARIANT_STOCK);
      const existingIndicator = stockContainer
        ? this.role(ROLES.STOCK_INDICATOR, stockContainer)
        : this.role(ROLES.STOCK_INDICATOR);

      if (existingIndicator) {
        existingIndicator.replaceWith(freshIndicator);
      }

      // Enforce MIN_LOADING before removing skeleton
      await this.awaitLoading(startTime);
      panelState.remove(STATES.LOADING);

      // Tear down old timer + click handler before re-init on fresh DOM
      this.#teardownRefreshTimer();

      // Re-init timer on fresh DOM elements
      this.#startRefreshTimer();

    } catch (error) {
      await this.awaitLoading(startTime);
      panelState.remove(STATES.LOADING);
      this.#notify(NOTIFICATION_TYPES.ERROR, this.#labels.stock_refresh_error);
      this.#teardownRefreshTimer();
      this.#startRefreshTimer();
    } finally {
      this.#isRefreshing = false;
    }
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * PUBLIC API
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Get current variant
   * @returns {Object|null}
   */
  getVariant() {
    return this.#productHandle?.getVariant() || null;
  }

  /**
   * Select variant by ID
   * @param {number} variantId
   * @returns {Object} { success, variant }
   */
  selectVariant(variantId) {
    if (!this.#productHandle) return { success: false, variant: null };
    return this.#productHandle.selectVariant(variantId);
  }

  /**
   * Select option value
   * @param {number} optionIndex
   * @param {string} value
   * @returns {Object} { success, variant }
   */
  selectOption(optionIndex, value) {
    if (!this.#productHandle) return { success: false, variant: null };
    return this.#productHandle.selectOption(optionIndex, value);
  }

  /**
   * Open variant panel (if exists)
   */
  openVariantPanel() {
    this.#variantPanel?.open();
  }

  /**
   * Close variant panel (if exists)
   */
  closeVariantPanel() {
    this.#variantPanel?.close();
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
 * REGISTER WEB COMPONENT
 * ═══════════════════════════════════════════════════════════════════════════ */

customElements.define('liftr-product', LiftrProduct);

export { LiftrProduct, ROLES, STATES, EVENTS, LISTEN, SETTINGS, PROPERTIES };